import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaShieldAlt, FaCalendarAlt, FaHandPointUp } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import { DATE_FORMAT_DEFAUT } from 'Config';
import styles from './PrivacyStatusBadge.module.scss';

type Props = {
    type?: string;
    activatedByName?: string | null;
    activatedAt?: string | null;
    resumesAt?: string | null;
    className?: string;
};

const TIME_FORMAT = 'HH:mm';
const DATE_TIME_TOOLTIP = `${DATE_FORMAT_DEFAUT} | ${TIME_FORMAT}`;

const PrivacyStatusBadge: React.FC<Props> = ({ type, activatedByName, activatedAt, resumesAt, className }: Props) => {
    const { t } = useTranslation();
    const tooltipId = React.useMemo(() => `privacy-tip-${Math.random().toString(36).slice(2, 9)}`, []);

    const isSchedule = type === 'schedule';
    const ReasonIcon = isSchedule ? FaCalendarAlt : FaHandPointUp;

    const reasonText = isSchedule
        ? resumesAt
            ? t('privacy.gps_paused.reason_schedule_with_resume' as any, { time: moment(resumesAt).format(TIME_FORMAT) }) as string
            : t('privacy.gps_paused.reason_schedule' as any) as string
        : activatedByName
            ? t('privacy.gps_paused.reason_toggle_by' as any, { name: activatedByName }) as string
            : t('privacy.gps_paused.reason_toggle' as any) as string;

    const tooltipLines: string[] = [];
    if (activatedByName && activatedAt) {
        tooltipLines.push(`<b>${t('privacy.gps_paused.activated_by' as any) as string}:</b> ${activatedByName} ${t('privacy.gps_paused.on_date' as any) as string} ${moment(activatedAt).format(DATE_TIME_TOOLTIP)}`);
    } else if (activatedAt) {
        tooltipLines.push(`<b>${t('privacy.gps_paused.activated_on' as any) as string}:</b> ${moment(activatedAt).format(DATE_TIME_TOOLTIP)}`);
    }
    if (resumesAt) {
        tooltipLines.push(`<b>${t('privacy.gps_paused.resumes_at' as any) as string}:</b> ${moment(resumesAt).format(DATE_TIME_TOOLTIP)}`);
    } else {
        tooltipLines.push(`<b>${t('privacy.gps_paused.resumes_at' as any) as string}:</b> ${t('privacy.gps_paused.no_deadline' as any) as string}`);
    }
    const tooltipHtml = tooltipLines.join('<br/>');

    return (
        <>
            <div
                className={`${styles.container} ${className ?? ''}`}
                data-tip={tooltipHtml}
                data-html={true}
                data-for={tooltipId}
            >
                <div className={styles.icon}>
                    <FaShieldAlt />
                </div>
                <div className={styles.content}>
                    <div className={styles.title}>{t('privacy.gps_paused.title') as string}</div>
                    <div className={styles.reason}>
                        <ReasonIcon className={styles.reasonIcon} />
                        <span>{reasonText}</span>
                    </div>
                </div>
            </div>
            <ReactTooltip id={tooltipId} html={true} place="top" effect="solid" />
        </>
    );
};

export default PrivacyStatusBadge;
